/**
 * Application configuration
 *
 * This module provides a type-safe way to access environment variables
 * across different environments (development, production, etc.)
 */

interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
  databaseURL?: string;
}

interface ProductUrls {
  DATON: string;
  PULSE: string;
  SARAS: string;
}

interface AppConfig {
  // Application information
  productType: string;
  productName: string;
  environmentType: string;
  stage?: string;
  release?: string;

  // API URLs
  apiUrl: string;
  iqApiUrl: string;
  subscriptionApiUrl: string;
  insightsSubscriptionApiUrl: string;
  userServiceApiUrl: string;
  datonApiUrl: string;
  supportApiUrl: string;
  sourceApiUrl: string;

  // User and application IDs
  userId: number;
  pulseId: number;

  // Licensing
  agGridLicense: string;

  // Firebase configuration
  firebaseConfig: FirebaseConfig;

  // Access control
  allowedSubscriptionRoles: string[];

  // Feature flags
  oldCdkSources: string[];
  /** Daton ``CDK_ENABLED_SOURCES`` — types that use ``/u/add-source-new`` (see datonCdkEnabledSources.ts). */
  datonCdkEnabledSources: string[];

  // External services
  marketingPricingPageLink: string;
  chargeBeeSite: string;
  // CC-C2: docs/videos for the Context Console — blank until set, the link
  // that reads it just doesn't render (see ContextConsole.tsx).
  contextConsoleResourcesLink?: string;
  datonBigQueryWarehouseId?: string;

  // Product URLs
  products: ProductUrls;

  // Environment information
  isProduction: boolean;
  environment: string;
  isDevelopment: boolean;
}

import { resolveApiUrl } from "./localDev";

/**
 * Parse comma-separated string to array
 */
function parseStringArray(value: string | undefined): string[] {
  if (!value) return [];
  return value.split(",").map((item) => item.trim());
}

/**
 * Central configuration object that provides access to all environment variables
 * with proper typing and fallback values
 */
const config: AppConfig = {
  // Application information
  productType: (import.meta.env.VITE_PRODUCT_TYPE as string) || "INSIGHTS",
  productName: (import.meta.env.VITE_PRODUCT_NAME as string) || "Pulse",
  environmentType: (import.meta.env.VITE_ENVIRONMENT_TYPE as string) || "dev",
  stage: import.meta.env.VITE_STAGE as string,
  release: import.meta.env.VITE_RELEASE as string,

  // API URLs. In local-dev mode (the `localdev` build mode, i.e. `pnpm local` →
  // `import.meta.env.MODE === "localdev"`; see src/config/localDev.ts) every
  // non-IQ service is routed at the local backend so the app depends only on
  // localhost:8000. There is no `VITE_LOCAL_DEV` env var — the gate is the mode.
  apiUrl: resolveApiUrl(import.meta.env.VITE_API_URL as string),
  iqApiUrl: import.meta.env.VITE_IQ_API_URL as string,
  subscriptionApiUrl: resolveApiUrl(
    import.meta.env.VITE_SUBSCRIPTION_API_URL as string
  ),
  insightsSubscriptionApiUrl: resolveApiUrl(
    import.meta.env.VITE_INSIGHTS_SUBSCRIPTION_API_URL as string
  ),
  userServiceApiUrl: resolveApiUrl(
    import.meta.env.VITE_USER_SERVICE_API_URL as string
  ),
  datonApiUrl: resolveApiUrl(import.meta.env.VITE_DATON_API_URL as string),
  supportApiUrl: resolveApiUrl(import.meta.env.VITE_SUPPORT_API_URL as string),
  sourceApiUrl: resolveApiUrl(import.meta.env.VITE_SOURCE_API_URL as string),

  // User and application IDs
  userId: Number(import.meta.env.VITE_USER_ID) || 1,
  pulseId: Number(import.meta.env.VITE_PULSE_ID) || 529319,

  // Licensing
  agGridLicense: import.meta.env.VITE_AG_GRID_LICENSE as string,

  // Firebase configuration
  firebaseConfig: {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string,
    messagingSenderId: import.meta.env
      .VITE_FIREBASE_MESSAGING_SENDER_ID as string,
    appId: import.meta.env.VITE_FIREBASE_APP_ID as string,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID as string,
    databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL as string,
  },

  // Access control
  allowedSubscriptionRoles: parseStringArray(
    import.meta.env.VITE_ALLOWED_SUBSCRIPTION_ROLES as string
  ),

  // Feature flags
  oldCdkSources: parseStringArray(
    import.meta.env.VITE_OLD_CDK_SOURCES as string
  ),
  datonCdkEnabledSources: parseStringArray(
    import.meta.env.VITE_DATON_CDK_ENABLED_SOURCES as string
  ),

  // External services
  marketingPricingPageLink: import.meta.env
    .VITE_MARKETING_PRICING_PAGE_LINK as string,
  chargeBeeSite: import.meta.env.VITE_CHARGEBEE_SITE as string,
  contextConsoleResourcesLink: import.meta.env
    .VITE_CONTEXT_CONSOLE_RESOURCES_LINK as string | undefined,

  // Product URLs
  products: {
    DATON: import.meta.env.VITE_PRODUCT_DATON as string,
    PULSE: import.meta.env.VITE_PRODUCT_PULSE as string,
    SARAS: import.meta.env.VITE_PRODUCT_SARAS as string,
  },

  // Daton datawarehouse id of BigQuery (opens its configuration page from iQ setup)
  datonBigQueryWarehouseId: import.meta.env.VITE_DATON_BIGQUERY_WAREHOUSE_ID as
    | string
    | undefined,

  // Environment information
  isProduction: import.meta.env.VITE_PRODUCTION === "true",
  environment: import.meta.env.MODE,
  isDevelopment: import.meta.env.DEV,
};

if (!config.iqApiUrl) {
  console.warn('[Config] VITE_IQ_API_URL is not set — feedback and chat API calls will fail in this environment');
}

export default config;

/**
 * Hook for loading runtime configuration that can be changed without rebuilding the app
 * This is useful for configuration that might change after deployment
 */
export async function loadRuntimeConfig(): Promise<Record<string, any>> {
  try {
    const response = await fetch("/config.json");
    if (!response.ok) {
      throw new Error(`Failed to load runtime config: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error loading runtime configuration:", error);
    return {};
  }
}
